import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { HStackProps } from '@rtkwlf/fenrir-react';
import { Button, Heading, HStack, Text, VStack } from '@rtkwlf/fenrir-react';
import { faFilter } from '@rtkwlf/pro-solid-svg-icons/faFilter';
import { useTranslation } from 'react-i18next';

import { ExportButtonWithAPICall } from '@packages/components/exportData/ExportButtonWithAPICall';
import { SimpleLoader } from '@packages/components/simple-loader/simple-loader';
import { INACTIVE_TAB_INDEX } from 'ae-constants/constants';

interface DetailsPageHeaderProps extends HStackProps {
  filter?: boolean;
  onFilterChange?: (filterState: boolean) => void;

  /** string label to be used for page title */
  title?: string;
  caption?: string;
  header?: React.ReactNode;

  /** by default, export button won't be shown */
  showExport?: boolean;

  children?: React.ReactElement;

  // To show loader in header
  isLoading?: boolean;

  exportButtonProps?: Omit<
    React.ComponentProps<typeof ExportButtonWithAPICall>,
    'apiPath'
  > &
    Partial<
      Pick<React.ComponentProps<typeof ExportButtonWithAPICall>, 'apiPath'>
    >;
}

export const DetailsPageHeader = ({
  title,
  header,
  children = undefined,
  filter = false,
  onFilterChange,
  showExport = false,
  isLoading,
  caption,
  exportButtonProps,
  ...rest
}: DetailsPageHeaderProps) => {
  const { t } = useTranslation();

  const toggleFilterFn = () => onFilterChange?.(!filter);

  return (
    <HStack width="full" paddingBottom="medium" {...rest}>
      {header || (
        <VStack>
          {title ? <Heading level="3">{title}</Heading> : null}
          {caption ? (
            <Text width="45rem" styledAs="body3">
              {caption}
            </Text>
          ) : null}
        </VStack>
      )}
      {isLoading && <SimpleLoader />}

      <HStack marginLeft="auto" yAlign="top">
        {onFilterChange ? (
          <Button
            variant="tertiary"
            title={filter ? t('filter:hideFilter') : t('filter:showFilter')}
            iconLeft={<FontAwesomeIcon icon={faFilter} />}
            data-testid="filterIcon"
            onClick={toggleFilterFn}
            tabIndex={INACTIVE_TAB_INDEX}
          >
            {filter ? t('filter:hideFilter') : t('filter:showFilter')}
          </Button>
        ) : null}

        {showExport && (
          <ExportButtonWithAPICall
            apiPath="/api/v1/query/execute"
            buttonType="secondary"
            {...exportButtonProps}
          />
        )}

        {children}
      </HStack>
    </HStack>
  );
};
